import { useRef, useState } from "react";
import { api, ApiError } from "@/lib/api";
import type { ExtractedPDF } from "@shared/types";
import { FileUp, Loader2 } from "lucide-react";

interface Props {
  bdrId: string;
  onExtracted: (extracted: ExtractedPDF) => void;
  onSwitchManual: () => void;
}

const MAX_BYTES = 25 * 1024 * 1024;

export function PDFUploader({ bdrId, onExtracted, onSwitchManual }: Props) {
  const [status, setStatus] = useState<"idle" | "uploading" | "extracting" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    setError(null);
    if (file.type !== "application/pdf") {
      setError("Please upload a .pdf file.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("File is too large (25 MB max).");
      return;
    }
    try {
      setStatus("uploading");
      const { url, r2_key } = await api.presignUpload(bdrId, file.name, file.type);
      const up = await fetch(url, { method: "PUT", headers: { "Content-Type": "application/pdf" }, body: file });
      if (!up.ok) throw new Error(`Upload failed (${up.status})`);

      setStatus("extracting");
      const extracted = await api.extractPDF(bdrId, r2_key);
      onExtracted(extracted);
    } catch (e) {
      setStatus("error");
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  function onDrop(ev: React.DragEvent) {
    ev.preventDefault();
    const file = ev.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  return (
    <div className="card p-6">
      <h2 className="section-header">Upload PDF handoff</h2>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-periwinkle/60 rounded-lg p-10 text-center cursor-pointer hover:bg-[rgba(117,115,247,0.06)] transition"
      >
        {status === "idle" && (
          <>
            <FileUp className="mx-auto text-periwinkle mb-3" size={36} />
            <div className="font-bold text-midnight mb-1">
              Drop your lead handoff PDF here
            </div>
            <div className="text-sm text-grape">or click to browse · max 25 MB</div>
          </>
        )}
        {status === "uploading" && (
          <div className="flex flex-col items-center text-grape">
            <Loader2 className="animate-spin text-periwinkle mb-3" size={28} />
            Uploading PDF…
          </div>
        )}
        {status === "extracting" && (
          <div className="flex flex-col items-center text-grape">
            <Loader2 className="animate-spin text-periwinkle mb-3" size={28} />
            Extracting fields — this usually takes 5–15 seconds…
          </div>
        )}
        {status === "error" && (
          <div className="text-fuscia font-bold">{error ?? "Something went wrong."}</div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
        />
      </div>

      {error && status !== "error" && <div className="error-text mt-3">{error}</div>}

      <div className="text-center mt-4">
        <button onClick={onSwitchManual} className="btn-ghost">
          Prefer to type? Enter manually →
        </button>
      </div>
    </div>
  );
}
